import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { ArrowLeft, ArrowRight, Shield, Check } from 'lucide-react';

const Consent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useTheme();
  const [accepted, setAccepted] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    loadAssessment();
  }, [id]);

  const loadAssessment = async () => {
    try {
      const token = localStorage.getItem('assessmentToken');
      const response = await api.get(`/assessments/access/${token}`);
      setClientData(response.data);
    } catch (error) {
      console.error('Failed to load assessment:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) return;

    try {
      await api.post('/consents', {
        assessment_id: id,
        client_id: clientData?.client?.id,
        consent_text: 'Client accepts privacy notice and assessment terms',
        ip_address: 'client',
        user_agent: navigator.userAgent
      });

      navigate(`/assessment/${id}/investment-questions`);
    } catch (error) {
      console.error('Failed to record consent:', error);
      setSubmitError(error.response?.data?.error || error.message || 'Failed to record consent');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#10b98120' }}>
                <Shield className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2" style={{ color: theme.text }}>
                Privacy & Consent
              </h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>
                Please read and accept the following
              </p>
            </div>

            <div className="space-y-4 mb-8 p-6 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400" />
                <p className="text-sm" style={{ color: theme.text }}>
                  Your information will be used for investment onboarding purposes only
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400" />
                <p className="text-sm" style={{ color: theme.text }}>
                  Your answers will help your advisor understand your investment needs and preferences
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400" />
                <p className="text-sm" style={{ color: theme.text }}>
                  This assessment is not a guarantee of investment performance
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400" />
                <p className="text-sm" style={{ color: theme.text }}>
                  A qualified advisor must review and approve any final investment recommendation
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400" />
                <p className="text-sm" style={{ color: theme.text }}>
                  The firm may store this assessment for compliance and regulatory purposes
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg mb-8" style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Privacy Notice:</strong> Your personal information is protected under POPIA 
                (Protection of Personal Information Act) and will be handled confidentially.
              </p>
            </div>

            {submitError && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{submitError}</p>}

            <form onSubmit={handleSubmit} noValidate>
              <label className="flex items-start gap-3 p-4 rounded-lg cursor-pointer mb-6 transition-colors hover:opacity-80" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  required
                />
                <span className="text-sm" style={{ color: theme.text }}>
                  I have read and understand the above. I consent to the collection and use of my information for this investment assessment.
                </span>
              </label>

              <div className="flex justify-between">
                <Button type="button" variant="ghost" onClick={() => navigate(-1)} className="flex items-center whitespace-nowrap">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button type="submit" disabled={!accepted} className="flex items-center whitespace-nowrap">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Consent;
